import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import CafeCard from "@/components/CafeCard";
import CafeMap from "@/components/CafeMap";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Map as MapIcon, LayoutGrid, Navigation } from "lucide-react";
import { toast } from "sonner";
import useDocumentMeta from "@/hooks/useDocumentMeta";

function haversine(lat1, lng1, lat2, lng2) {
  const R = 6371; // km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const TYPE_OPTIONS = [
  { value: "all", label: "All spots" },
  { value: "cafe", label: "Cafés" },
  { value: "bean_shop", label: "Bean shops" },
  { value: "both", label: "Café + Bean shop" },
];

const AMENITIES = [
  { value: "all", label: "Any amenity" },
  { value: "wifi", label: "Wi-Fi" },
  { value: "outdoor_seating", label: "Outdoor seating" },
  { value: "vegan_milk", label: "Vegan milk" },
  { value: "laptop_friendly", label: "Laptop friendly" },
  { value: "pet_friendly", label: "Pet friendly" },
  { value: "whole_bean", label: "Whole bean" },
  { value: "tasting_bar", label: "Tasting bar" },
  { value: "classes", label: "Classes" },
];

const VIBES = [
  { value: "all", label: "Any vibe" },
  { value: "quiet_study", label: "Quiet / study" },
  { value: "first_date", label: "First date" },
  { value: "work_friendly", label: "Work-friendly" },
  { value: "espresso_bar", label: "Espresso bar" },
  { value: "family", label: "Family" },
  { value: "outdoor", label: "Outdoor" },
];

export default function DirectoryPage() {
  const [params, setParams] = useSearchParams();
  useDocumentMeta({
    title: "Directory",
    description: "Search every café and specialty bean shop on BrewFinder by name, city, vibe, amenity, or country.",
  });
  const [cafes, setCafes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("split"); // split | grid | map
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("type") || "all");
  const [amenity, setAmenity] = useState(params.get("amenity") || "all");
  const [country, setCountry] = useState(params.get("country") || "all");
  const [vibe, setVibe] = useState(params.get("vibe") || "all");
  const [userLoc, setUserLoc] = useState(null);
  const [locating, setLocating] = useState(false);

  const fetchCafes = async () => {
    setLoading(true);
    try {
      const qs = {};
      if (q) qs.q = q;
      if (type && type !== "all") qs.type = type;
      if (amenity && amenity !== "all") qs.amenity = amenity;
      if (country && country !== "all") qs.country = country;
      if (vibe && vibe !== "all") qs.vibe = vibe;
      const { data } = await api.get("/cafes", { params: qs });
      setCafes(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/countries").then(({ data }) => setCountries(data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchCafes();
    const next = {};
    if (q) next.q = q;
    if (type !== "all") next.type = type;
    if (amenity !== "all") next.amenity = amenity;
    if (country !== "all") next.country = country;
    if (vibe !== "all") next.vibe = vibe;
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, amenity, country, vibe]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    fetchCafes();
    const next = {};
    if (q) next.q = q;
    if (type !== "all") next.type = type;
    if (amenity !== "all") next.amenity = amenity;
    if (country !== "all") next.country = country;
    if (vibe !== "all") next.vibe = vibe;
    setParams(next, { replace: true });
  };

  const count = cafes.length;

  const useNearMe = () => {
    if (!("geolocation" in navigator)) {
      toast("Geolocation isn't supported on this device.");
      return;
    }
    if (userLoc) {
      setUserLoc(null);
      toast("Sorted by rating again.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
        toast("Sorted by distance from you.");
      },
      (err) => {
        setLocating(false);
        toast("Couldn't get your location", { description: err.message });
      },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const displayedCafes = React.useMemo(() => {
    if (!userLoc) return cafes;
    return [...cafes]
      .map((c) => ({
        ...c,
        distance_km: haversine(userLoc.lat, userLoc.lng, c.latitude, c.longitude),
      }))
      .sort((a, b) => a.distance_km - b.distance_km);
  }, [cafes, userLoc]);

  const grid = useMemo(
    () => (
      <div className="grid sm:grid-cols-2 gap-6">
        {displayedCafes.map((c) => (
          <div key={c.id} className="relative">
            {userLoc && (
              <span
                className="absolute z-10 top-3 left-3 text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[#2C1E16] text-[#F9F6F0] rounded-full"
                data-testid={`distance-${c.id}`}
              >
                {c.distance_km < 1 ? `${Math.round(c.distance_km * 1000)} m` : `${c.distance_km.toFixed(1)} km`}
              </span>
            )}
            <CafeCard cafe={c} />
          </div>
        ))}
      </div>
    ),
    [displayedCafes, userLoc]
  );

  return (
    <div data-testid="directory-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">The Directory</p>
        <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">
          Every café & bean shop worth knowing.
        </h1>
        <p className="text-[#786B63] mt-3 max-w-2xl">
          {count} {count === 1 ? "spot" : "spots"} curated by the community.
        </p>

        {/* Filters */}
        <form
          onSubmit={onSearchSubmit}
          className="mt-8 border border-border bg-card rounded-sm p-4 flex flex-wrap items-center gap-3"
        >
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#786B63]" strokeWidth={1.5} />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, city, or specialty…"
              className="pl-9 rounded-sm bg-[#F9F6F0] border-border h-11"
            />
          </div>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="rounded-sm bg-[#F9F6F0] h-11 w-[160px]" data-testid="filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-[#F9F6F0]">
              {TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={amenity} onValueChange={setAmenity}>
            <SelectTrigger className="rounded-sm bg-[#F9F6F0] h-11 w-[180px]" data-testid="filter-amenity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-[#F9F6F0]">
              {AMENITIES.map((a) => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="rounded-sm bg-[#F9F6F0] h-11 w-[160px]" data-testid="filter-country">
              <SelectValue placeholder="All countries" />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-[#F9F6F0]">
              <SelectItem value="all">All countries</SelectItem>
              {countries.map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {c.name} · {c.count}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={vibe} onValueChange={setVibe}>
            <SelectTrigger className="rounded-sm bg-[#F9F6F0] h-11 w-[150px]" data-testid="filter-vibe">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-sm bg-[#F9F6F0]">
              {VIBES.map((v) => (
                <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="submit"
            className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0] h-11 px-6"
            data-testid="search-submit"
          >
            Search
          </Button>
          <Button
            type="button"
            onClick={useNearMe}
            disabled={locating}
            data-testid="near-me-button"
            variant="outline"
            className={`rounded-sm h-11 px-4 gap-2 ${userLoc ? "border-[#C05A46] text-[#C05A46]" : ""}`}
          >
            <Navigation size={15} strokeWidth={1.5} />
            {locating ? "Locating…" : userLoc ? "Clear distance" : "Near me"}
          </Button>
          <div className="flex border border-border rounded-sm overflow-hidden bg-[#F9F6F0] h-11 ml-auto">
            <button
              type="button"
              onClick={() => setView("split")}
              data-testid="view-split"
              className={`px-3 text-xs uppercase tracking-wider ${view === "split" ? "bg-[#2C1E16] text-[#F9F6F0]" : "text-[#2C1E16]"}`}
            >
              Split
            </button>
            <button
              type="button"
              onClick={() => setView("grid")}
              data-testid="view-grid"
              className={`px-3 grid place-items-center ${view === "grid" ? "bg-[#2C1E16] text-[#F9F6F0]" : "text-[#2C1E16]"}`}
              aria-label="Grid"
            >
              <LayoutGrid size={16} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setView("map")}
              data-testid="view-map"
              className={`px-3 grid place-items-center ${view === "map" ? "bg-[#2C1E16] text-[#F9F6F0]" : "text-[#2C1E16]"}`}
              aria-label="Map"
            >
              <MapIcon size={16} strokeWidth={1.5} />
            </button>
          </div>
        </form>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pb-20">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="border border-border bg-card aspect-[4/5] rounded-sm animate-pulse" />
            ))}
          </div>
        ) : cafes.length === 0 ? (
          <div className="border border-border bg-card rounded-sm p-16 text-center">
            <p className="font-serif text-2xl">No matches yet.</p>
            <p className="text-sm text-[#786B63] mt-2">Try a different search or amenity.</p>
          </div>
        ) : view === "grid" ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayedCafes.map((c) => (
              <div key={c.id} className="relative">
                {userLoc && (
                  <span
                    className="absolute z-10 top-3 left-3 text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[#2C1E16] text-[#F9F6F0] rounded-full"
                  >
                    {c.distance_km < 1 ? `${Math.round(c.distance_km * 1000)} m` : `${c.distance_km.toFixed(1)} km`}
                  </span>
                )}
                <CafeCard cafe={c} />
              </div>
            ))}
          </div>
        ) : view === "map" ? (
          <CafeMap cafes={displayedCafes} height="h-[calc(100vh-220px)]" />
        ) : (
          <div className="grid md:grid-cols-[1fr_520px] gap-8">
            <div>{grid}</div>
            <div className="md:sticky md:top-20 self-start">
              <CafeMap cafes={displayedCafes} height="h-[calc(100vh-160px)]" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
